<template>
  <div class="lazy-video" @mouseover="toggleVideoPlay" @mouseleave="toggleVideoStop">
    <lazy-image :class="['lazy-video__thumbnail', {'lazy-video__thumbnail--hidden': !showThumbnail}]" :src="thumbnailSrc" :parent-container="parentContainer"/>
    <video
      class="lazy-video__video"
      ref="vid"
      muted
      loop
      preload="metadata"
    />
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue'
import LazyImage from './LazyImage.vue'

const props = withDefaults(defineProps<{
  src: string
  thumbnailSrc: string
  parentContainer?: HTMLElement | null
}>(), {
  parentContainer: null
})

const emits = defineEmits<{
  (event: 'viewportIntersect'): void
}>()

const showThumbnail = ref(true)
const vid = ref<HTMLVideoElement | null>(null)
let playPromise: Promise<void> | null = null
const resourceAvailable = ref(true)

onMounted(() => {
  if (vid.value) {
    const video = vid.value
    new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // video is entering the viewport
            emits('viewportIntersect')
          } else if (video.src !== '') {
            // unload the video when it leaves the viewport
            if (playPromise) {
              playPromise.then(() => {
                unloadVideo()
              })
            } else {
              unloadVideo()
            }
          }
        })
      },
      {
        // Observe intersection with the viewport
        root: props.parentContainer,
        // We may want to adjust this to allow images to start loading slightly earlier than being in the viewport
        rootMargin: '300px 0px 300px 0px'
      }
    ).observe(vid.value)
  }
})

const unloadVideo = () => {
  if (vid.value && vid.value.src !== '' && resourceAvailable.value) {
    showThumbnail.value = true
    vid.value.pause()
    // src attribute must be a valid non-empty string, so must be removed to unload the video
    vid.value.removeAttribute('src')
    vid.value.load()
  }
}

const toggleVideoPlay = () => {
  if (vid.value) {
    if (vid.value.src === '') {
      vid.value.src = props.src
      vid.value.load()
    }
    vid.value.currentTime = 0

    playPromise = vid.value.play()
      .then(() => { showThumbnail.value = false })
      .catch(() => { resourceAvailable.value = false })
  }
}

const toggleVideoStop = () => {
  if (vid.value && resourceAvailable.value) {
    showThumbnail.value = true
    // Play function is asynchronous, and will throw error when interrupted
    if (playPromise) {
      playPromise.then(() => {
        if (vid.value) {
          vid.value.pause()
        }
      })
    }
  }
}

</script>
<style lang="scss">
.lazy-video {
  position: relative;

  &__thumbnail,
  &__video {
    height: inherit;
    width: inherit;
    border-radius: inherit;
  }

  &__thumbnail {
    object-fit: cover;
    position: absolute;
    z-index: 1;
    visibility: visible; // required to toggle visibility as to not trigger intersection observer

    &--hidden {
      visibility: hidden;
    }
  }
}
</style>
